<template>
  <div v-if="loading"><spinner /></div>
  <div v-else class="performance-wrapper">
    <div class="title-tabs-container">
      <h1 class="title-screen" style="font-size: 20px">Performances</h1>
      <div class="tabs-container">
        <div
          class="tab"
          :style="activeStep === 1 && 'background: #0277BD; color: white'"
          @click="handleShowLiveEvents()"
        >
          Live
        </div>
        <div
          class="tab"
          :style="activeStep === 2 && 'background: #0277BD; color: white'"
          @click="handleShowPastEvents()"
        >
          Past
        </div>
      </div>
    </div>
    <div v-for="(performance, index) in $v.form.$each.$iter" :key="performance.id">
      <performance
        v-if="!performance.deletedAt"
        :initial-values="performance"
        :index="parseInt(index)"
        :sold-at-least-once="performance.$model.soldAtLeastOnce || activeStep === 2"
        :sold-out="performance.$model.soldOut"
        :order-item-template-ids-sold-at-least-once="
          performance.$model.orderItemTemplateIdsSoldAtLeastOnce
        "
        :organization-slug="organizationSlug"
        :venues="venues"
        :new-event-modal="false"
        :ticket-types="ticketTypes"
        :promo-codes="promoCodes"
        :vs="performance"
        :ticket-sellers="ticketSellers"
        @duplicatePerformance="handleDuplicatePerformance"
        @refetchOrderItemAndVenue="handleRefetchOrderItemAndVenue"
        @removePerformance="removePerformance"
        @removeSelectedTicketSellers="removeSelectedTicketSellers($event)"
      />
    </div>
    <main-button
      v-if="activeStep === 1"
      style="margin-top: 8px"
      flat
      depressed
      @click="addPerformance"
    >
      <span class="add-symbol">+</span>Add date
    </main-button>
    <div v-if="activeStep === 1" class="buttons-wrapper" style="margin-top: 58px">
      <main-button text="CANCEL" flat depressed @click="goBack()" />
      <div class="buttons">
        <main-button text="UPDATE" @click="updatePerformance()" />
      </div>
    </div>
    <simple-snackbar
      :open.sync="snackbarOpen"
      :message-prop="snackbarMessage"
      :color="snackbarColor"
    />
  </div>
</template>

<script>
import performance from './performance.vue'
import moment from 'moment'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES_AND_PROMO_CODES } from '@/graphql/orderItemTemplate.graphql'
import {
  GET_EVENTS_WITH_TICKET_SELLER_ORGANIZATIONS_BY_EVENT_TEMPLATE_ID,
  UPDATE_EVENT_WITH_EVENT_TEMPLATE_ID,
} from '@/graphql/event.graphql'
import Spinner from '../shared/spinner.vue'
import { minLength, required } from 'vuelidate/lib/validators'
import { v4 as uuid } from 'uuid'
import { omit } from 'lodash'
import { GET_NON_BOX_OFFICE_TICKET_SELLERS } from '@/graphql/user.graphql'
import { uniq } from 'lodash'

export default {
  name: 'performance-container',
  components: {
    performance,
    Spinner,
    SimpleSnackbar,
  },
  props: {
    organizationSlug: String,
    eventTemplateId: String,
  },
  data() {
    return {
      form: {},
      venues: [],
      activeStep: 1,
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarColor: 'primary',
      allPerformances: [],
      loading: true,
      ticketTypes: [],
      promoCodes: [],
      ticketSellers: [],
    }
  },
  async mounted() {
    await this.fetchTicketTypesAndVenues()
    await this.fetchEvents()
    await this.fetchTicketSellers()
  },
  validations() {
    return {
      form: {
        $each: {
          date: {
            required,
          },
          time: {
            required,
          },
          performanceType: {
            required,
          },
          //TODO add this validator if venue has no numeration
          audienceCapacity: {
            required(v, obj) {
              let hasNoNumeration = !!this.venues.find(
                v => v.id === obj.venueId && (!v.seats || !v.predefinedSections),
              )

              return hasNoNumeration ? required(v) : required(!v)
            },
          },
          orderItemTemplateIds: {
            minLength: minLength(1),
            required,
          },
        },
      },
    }
  },
  methods: {
    handleShowPastEvents() {
      this.activeStep = 2

      return (this.form = this.allPerformances.filter(performance =>
        moment(performance.date).isBefore(moment()),
      ))
    },
    handleShowLiveEvents() {
      this.activeStep = 1

      return (this.form = this.allPerformances.filter(performance =>
        moment(performance.date).isAfter(moment()),
      ))
    },
    handleDuplicatePerformance(performanceData) {
      const { soldOut, soldAtLeastOnce, orderItemTemplateIdsSoldAtLeastOnce, eventCode, ...rest } =
        performanceData
      this.form.push({ ...omit(rest, ['id']), id: uuid() })
    },
    async handleRefetchOrderItemAndVenue() {
      await this.fetchTicketTypesAndVenues()
    },
    addPerformance() {
      this.form.push({ id: uuid(), orderItemTemplateIds: [] })
    },
    removePerformance(id) {
      this.form = this.form.filter(perf => perf.id !== id)
    },
    modifyPerformanceData() {
      return this.form.map(event => {
        const {
          date,
          time,
          soldOut,
          soldAtLeastOnce,
          orderItemTemplateIdsSoldAtLeastOnce,
          eventCode,
          ...rest
        } = event
        const dateOnly = moment(date).format('DD.MM.YYYY')

        return { ...rest, from: `${moment(dateOnly + ' ' + time, 'DD.MM.YYYY HH:mm')}` }
      })
    },
    removePromoCodesFromPerformancesWithoutRegularTicket() {
      const allOrganizationRegularTicketIds = this.ticketTypes
        .filter(t => !t.membership && !t.ticketSeller && t.prepaidCoupon)
        .map(t => t.id)
      this.form = this.form.map(p => {
        if (p.orderItemTemplateIds.find(id => allOrganizationRegularTicketIds.includes(id))) {
          return p
        } else {
          return { ...p, promoCodeIds: [] }
        }
      })
    },
    async updatePerformance() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }
      this.removePromoCodesFromPerformancesWithoutRegularTicket()
      const eventInput = this.modifyPerformanceData()
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_EVENT_WITH_EVENT_TEMPLATE_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
          eventInput,
        },
      })
      if (errors) {
        this.snackbarOpen = true
        this.snackbarMessage = 'Fill in the required information'
        this.snackbarColor = 'error'

        return
      }
      this.snackbarOpen = true
      this.snackbarMessage = 'Event updated successfully!'
      this.snackbarColor = 'primary'
      this.allPerformances = []
      await this.fetchEvents()
    },
    async fetchTicketTypesAndVenues() {
      const {
        data: {
          getOrganizationOrderItemTemplates,
          getOrganizationVenues,
          getAllOrganizationPromoCodes,
        },
        errors,
      } = await this.$apollo.query({
        query: GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES_AND_PROMO_CODES,
        variables: {
          organizationSlug: this.organizationSlug,
          pagination: this.pagination,
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          message: 'Oops, Something went wrong!',
          color: 'error',
        }
      } else {
        this.venues = getOrganizationVenues.organizationVenues.map(venue => venue.venue)
        this.ticketTypes = getOrganizationOrderItemTemplates
        this.promoCodes = getAllOrganizationPromoCodes
      }
    },
    async fetchEvents() {
      const {
        data: { getEventsWithTicketSellerOrganizationsByEventTemplateId },
        errors,
      } = await this.$apollo.query({
        query: GET_EVENTS_WITH_TICKET_SELLER_ORGANIZATIONS_BY_EVENT_TEMPLATE_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          message: 'Oops, Something went wrong!',
          color: 'error',
        }
      } else {
        this.allPerformances = getEventsWithTicketSellerOrganizationsByEventTemplateId.map(
          event => {
            return {
              ...omit(event, ['slug', 'orderItems', 'title', 'location']),
              venueId: event.venueId || this.venues.find(v => v.name === event.location.venue).id,
              date: event.from,
              soldOut: event.orderItems.every(oi => parseInt(oi.quantity) === 0),
              soldAtLeastOnce: event.orderItems.some(
                oi => parseInt(oi.quantity) !== oi.initialQuantity,
              ),
              orderItemTemplateIdsSoldAtLeastOnce: event.orderItems
                .filter(oi => parseInt(oi.quantity) !== oi.initialQuantity)
                .map(oi => parseInt(oi.orderItemTemplateId)),
              time: `${moment(event.from).hours()}:${moment(event.from).minutes()}`,
              orderItemTemplateIds: event.orderItems.map(oi => parseInt(oi.orderItemTemplateId)),
              ticketSellerIds: uniq(
                event.orderItems
                  .map(oi => oi.ticketSellerOrganizations)
                  .flat()
                  .map(tso => parseInt(tso.id)),
              ),
            }
          },
        )
        this.handleShowLiveEvents()
        this.loading = false
      }
    },
    async fetchTicketSellers() {
      const {
        data: { getNonBoxOfficeTicketSellers: ticketSellers },
      } = await this.$apollo.query({
        query: GET_NON_BOX_OFFICE_TICKET_SELLERS,
        variables: { organizationSlug: this.organizationSlug },
      })

      this.ticketSellers = ticketSellers.map(ts => {
        return {
          ...ts,
          id: Number(ts.id),
        }
      })
    },
    removeSelectedTicketSellers(performanceId) {
      const performance = this.form.find(item => item.id === performanceId)
      if (!performance) {
        return
      }

      performance.ticketSellerIds = undefined
    },
  },
}
</script>

<style scoped>
.performance-wrapper {
  max-width: 700px;
}
.tabs-container {
  display: flex;
}
.tab {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  text-transform: uppercase;
  color: #0277bd;
  width: 78px;
  height: 36px;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.title-tabs-container {
  justify-content: space-between;
  display: flex;
}
</style>
